import type { EstadoStock, Producto, Unidad } from '../types'

const CLP = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
})

const NUM = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 2 })

export const money = (n: number) => CLP.format(Math.round(n || 0))

/** Para ejes de gráficos y tarjetas: $1,2M en vez de $1.200.000. */
export function moneyCorto(n: number) {
  const v = Math.abs(n)
  if (v >= 1_000_000) return `$${NUM.format(n / 1_000_000)}M`
  if (v >= 1_000) return `$${Math.round(n / 1_000)}k`
  return money(n)
}

export const num = (n: number) => NUM.format(n || 0)

export const pct = (n: number) => `${NUM.format(Math.round((n || 0) * 10) / 10)}%`

const UNIDADES: Record<Unidad, string> = {
  kg: 'kg',
  g: 'g',
  unidad: 'un',
  caja: 'caja',
  bandeja: 'band',
  litro: 'L',
}

export const unidadCorta = (u: Unidad) => UNIDADES[u] ?? u

export function pesoLabel(p: Pick<Producto, 'peso' | 'unidad'>) {
  if (!p.peso) return ''
  return `${num(p.peso)} ${unidadCorta(p.unidad)}`
}

/* ---------------- Fechas ---------------- */

const fmtFecha = new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
const fmtHora = new Intl.DateTimeFormat('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false })
const fmtDiaMes = new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short' })
const fmtLargo = new Intl.DateTimeFormat('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })

/** Convierte un texto ISO en Date sin correr el día.
 *
 *  Las fechas "a secas" —`2026-08-25`, como las de `fechaDespacho` o
 *  `vencimientoPago`— las lee JavaScript como medianoche UTC. En Chile
 *  eso son las 20:00 o 21:00 del día ANTERIOR, así que al mostrarlas
 *  salía siempre un día menos: el vendedor prometía el 25 y en la hoja
 *  de ruta decía 24. Poniéndoles el mediodía, cualquier huso horario
 *  del mundo cae dentro del mismo día.
 *
 *  Las que llevan hora (`fecha` de una venta, por ejemplo) son
 *  instantes de verdad y se dejan tal cual. */
const aFecha = (iso: string) => new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso)

export const fecha = (iso?: string) => (iso ? fmtFecha.format(aFecha(iso)) : '—')
export const hora = (iso?: string) => (iso ? fmtHora.format(aFecha(iso)) : '—')
export const fechaHora = (iso?: string) => (iso ? `${fecha(iso)} · ${hora(iso)}` : '—')
export const diaMes = (iso?: string) => (iso ? fmtDiaMes.format(aFecha(iso)) : '—')
export const fechaLarga = (iso?: string) => (iso ? fmtLargo.format(aFecha(iso)) : '—')

/** "hace 3 días", "en 2 semanas". */
export function relativo(iso?: string) {
  if (!iso) return '—'
  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
  const diff = aFecha(iso).getTime() - Date.now()
  const dias = Math.round(diff / 86_400_000)
  if (Math.abs(dias) < 1) {
    const horas = Math.round(diff / 3_600_000)
    if (Math.abs(horas) < 1) return rtf.format(Math.round(diff / 60_000), 'minute')
    return rtf.format(horas, 'hour')
  }
  if (Math.abs(dias) < 30) return rtf.format(dias, 'day')
  if (Math.abs(dias) < 365) return rtf.format(Math.round(dias / 30), 'month')
  return rtf.format(Math.round(dias / 365), 'year')
}

/** Fecha local en formato yyyy-mm-dd, sin desplazamiento por zona horaria. */
export function hoyISO(d = new Date()) {
  const off = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}

export function inicioDia(d = new Date()) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export function inicioSemana(d = new Date()) {
  const x = inicioDia(d)
  // La semana comercial en Chile parte el lunes.
  const dia = (x.getDay() + 6) % 7
  x.setDate(x.getDate() - dia)
  return x
}

export function inicioMes(d = new Date()) {
  const x = inicioDia(d)
  x.setDate(1)
  return x
}

export function inicioAno(d = new Date()) {
  const x = inicioMes(d)
  x.setMonth(0)
  return x
}

export function diasHasta(iso?: string) {
  if (!iso) return Infinity
  return Math.ceil((new Date(iso).getTime() - inicioDia().getTime()) / 86_400_000)
}

/* ---------------- Stock ---------------- */

export function umbral(p: Producto, global: number) {
  return p.stockMinimo ?? global
}

export function estadoStock(p: Producto, global: number): EstadoStock {
  const disponible = p.stock - p.reservado
  if (disponible <= 0) return 'agotado'
  if (disponible <= umbral(p, global)) return 'poco'
  return 'disponible'
}

export const ESTADO_STOCK_META: Record<
  EstadoStock,
  { etiqueta: string; punto: string; chip: string; barra: string }
> = {
  disponible: {
    etiqueta: 'Disponible',
    punto: 'bg-lima-500',
    chip: 'bg-lima-50 text-lima-700 dark:bg-lima-700/20 dark:text-lima-300',
    barra: 'bg-lima-500',
  },
  poco: {
    etiqueta: 'Poco stock',
    punto: 'bg-ambar-400',
    chip: 'bg-ambar-50 text-ambar-600 dark:bg-ambar-500/20 dark:text-ambar-300',
    barra: 'bg-ambar-400',
  },
  agotado: {
    etiqueta: 'Sin stock',
    punto: 'bg-rojo-500',
    chip: 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/20 dark:text-rojo-300',
    barra: 'bg-rojo-500',
  },
}

/* ---------------- RUT chileno ---------------- */

export function formatRut(valor: string) {
  const limpio = valor.replace(/[^0-9kK]/g, '').toUpperCase()
  if (limpio.length < 2) return limpio
  const cuerpo = limpio.slice(0, -1)
  const dv = limpio.slice(-1)
  return `${cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`
}

export function rutValido(valor: string) {
  const limpio = valor.replace(/[^0-9kK]/g, '').toUpperCase()
  if (limpio.length < 2) return false
  const cuerpo = limpio.slice(0, -1)
  const dv = limpio.slice(-1)
  let suma = 0
  let mult = 2
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += Number(cuerpo[i]) * mult
    mult = mult === 7 ? 2 : mult + 1
  }
  const resto = 11 - (suma % 11)
  const esperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto)
  return dv === esperado
}

/* ---------------- Texto ---------------- */

/** Normaliza para búsqueda: sin tildes, minúsculas. Así "pasteleria"
 *  encuentra "Pastelería". */
export const normalizar = (s: string) =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

export function iniciales(nombre: string) {
  return nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

export function nombreCompleto(c: { nombre: string; apellido?: string }) {
  return [c.nombre, c.apellido].filter(Boolean).join(' ')
}
